<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>
		<breadcrumbs :breadcrumbs="page.breadcrumbs" :class="page.headingImage ? '' : 'no-slider'" />

		<main-content
			:title="page.title || page.header"
			:subtitle="page.subtitle"
			:content="page.content"
			:introduction-content="page.introductionContent"
		/>

		<section class="main-content rooms-section background-light">
			<div class="row row-header align-center">
				<h2 class="subtitle">{{ defaults[locale].rooms.subtitle }}</h2>
				<h2>{{ defaults[locale].rooms.sectionRoomsTitle }}</h2>
				<div v-parse-links v-html="defaults[locale].rooms.sectionRoomsContent" />
			</div>
			<div v-for="(room, index) in roomsData" :key="room.pageID" class="row contentblock">
				<div
					class="columns column6 contentblock-content"
					:class="{ right: index % 2 == 0, left: index % 2 != 0 }"
				>
					<h2>{{ room.title || room.header }}</h2>
					<hr />
					<div v-parse-links v-html="room.content" />
					<div class="room-icons">
						<div v-for="facility in room.facilities" :key="facility.id" class="icon-holder">
							<font-awesome-icon :icon="`fal fa-${facility.icon}`" size="1x" />
							<span>{{ facility.content }}</span>
						</div>
					</div>
					<nuxt-link :to="localePath(`/${room.filename}`)" class="button button-no-fill">
						{{ $t('viewThisRoom') }}
					</nuxt-link>
					<book-button class="button button-cta" :roomid="room.bookingID" aria-label="Reserve now">
						<span>{{ $t('bookThisRoom') }}</span>
					</book-button>
				</div>
				<div class="contentblock-image">
					<picture>
						<source v-if="room.imageOverviewWebp" :srcset="room.imageOverviewWebp" type="image/webp" />
						<source :srcset="room.imageOverview" />
						<img loading="lazy" :src="room.imageOverview" :alt="room.imageOverviewAlt" />
					</picture>
				</div>
			</div>
		</section>

		<contentblocks
			v-if="page && page.contentblocks && page.contentblocks.length"
			:items="page.contentblocks"
			:content-blocks-content="page.sectionContentblocksContent"
			:content-blocks-subtitle="page.sectionContentblocksTitle"
		/>
		<frequently-asked-questions
			v-if="page && page.frequentlyAskedQuestion"
			:header="page.frequentlyAskedQuestion.header"
			:items="page.frequentlyAskedQuestion.faqitems"
		/>
		<pre-footer
			v-if="page && page.footerSection && page.footerSection.length"
			:footer-section="page.footerSection[0]"
		/>
		<newsletter
			v-if="page && page.showNewsletter"
			:header="defaults[locale].newsletter.title"
			:content="defaults[locale].newsletter.content"
			:button="defaults[locale].newsletter.button"
		/>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localePath = useLocalePath();
const config = useRuntimeConfig();

await fetchPage();
await fetchDefaults();

const { data: roomsData } = await useWebsiteFetch('rooms', {
	query: { language: locale.value },
	key: `${locale.value}/rooms`,
});

const currentUrl = config.public.siteUrl;

function stripHtml(html) {
	//eslint-disable-next-line
	if (typeof html !== 'string') return '';

	return html.replace(/<[^>]*>/g, '').trim();
}

const hotelRoomsSchema = {
	'@context': 'http://schema.org',
	'@id': `${currentUrl}#hotel`,
	containsPlace: (roomsData.value || []).map((room) => ({
		'@type': 'HotelRoom',
		name: room.header,
		description: stripHtml(room.content),
		image: room.imageOverview,
	})),
};

useHead({
	script: [
		{
			type: 'application/ld+json',
			innerHTML: JSON.stringify(hotelRoomsSchema),
		},
	],
});
</script>

<style lang="scss" scoped>
.contentblocks {
	background: #bf702a;
}

.contentblock {
	margin: 40px auto;
	position: relative;
}

.row-header {
	max-width: 1000px;
}

.contentblock-image {
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	width: 66%;
	z-index: 1;
}

.contentblock-content {
	background: rgb(249 246 242 / 80%);
	box-shadow: 0 18px 18px rgb(0 0 0 / 25%);
	backdrop-filter: blur(34px);
	padding: 60px 90px;
	margin: 60px auto;
	position: relative;
	z-index: 2;
}

.row:nth-child(even) .contentblock-image {
	left: 0;
	right: auto;
}

.contentblock-image img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.room-icons {
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-start;
	align-items: flex-start;
	margin: 0 0 40px;
}

.icon-holder {
	color: #8c5c51;
	width: 49%;
	margin: 10px 0;
	font-size: 16px;
}

.icon-holder i,
.icon-holder svg {
	margin: 0 5px 0 0;
}

.room-item .room-icons {
	max-width: 600px;
}

.room-info {
	padding: 0 0 50px;

	.row {
		padding: 50px 30px;
	}
}

@media (max-width: 1080px) {
	.icon-holder {
		width: 49%;
	}
}

@media (max-width: 920px) {
	.contentblock-image {
		width: 100%;
		position: relative;
		inset: auto;
	}

	.contentblock {
		display: flex;
		flex-flow: wrap column-reverse;
	}

	.contentblock-content {
		margin: 0 auto 50px;
		padding: 50px 30px;
	}

	.icon-holder {
		width: 49%;
	}
}

@media (max-width: 680px) {
	.icon-holder {
		width: 99%;
	}
}
</style>
